import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  NgZone,
  ChangeDetectionStrategy,
} from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MediaService } from '../../services/media/media.service';
import { FileItemDTO } from '../../dto/file-manager/file-item.dto';
import { UploadMetadataDTO } from '../../dto/upload/upload-metadata.dto';

@Component({
  selector: 'app-media-preview',
  standalone: true,
  imports: [MatProgressBarModule, MatIconModule, MatButtonModule],
  templateUrl: './media-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./media-preview.component.scss'],
})
export class MediaPreviewComponent implements OnInit, OnDestroy {
  public isLoading = false;
  public errorMessage: string | null = null;
  public obfuscatedName: string = '';
  public fileInfo: FileItemDTO | null = null;
  public blobUrl: string | null = null;
  public safeBlobUrl: SafeResourceUrl | null = null;
  public Math = Math;

  public showControls = true;
  public isVideoPlaying = false;
  public videoElement: HTMLVideoElement | null = null;
  public audioElement: HTMLAudioElement | null = null;
  public retryCount = 0;

  constructor(
    private mediaService: MediaService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.obfuscatedName = params['obfuscatedName'];
      if (this.obfuscatedName) {
        this.loadAndPreviewFile();
      }
    });
  }

  /**
   * Load and preview the file
   */
  async loadAndPreviewFile(): Promise<void> {
    if (!this.obfuscatedName) return;

    try {
      this.setLoading(true);
      this.errorMessage = null;
      this.retryCount = 0; // Reset retry counter

      // Get file metadata first using Upload endpoint
      this.fileInfo = await this.getFileMetadata(this.obfuscatedName);

      // Debug info removed for cleaner UI

      // Handle video streaming vs regular download
      const isVideoFile =
        this.fileInfo?.mime_type?.startsWith('video/') ||
        this.fileInfo?.is_video === true;

      if (isVideoFile) {
        // For videos, we don't download upfront - let streaming/fallback handle it
        this.blobUrl = null;
      } else {
        // Download regular files (images, PDFs, etc.) as blob
        const blob = await this.mediaService.downloadFile(
          this.obfuscatedName,
          (progress) => {
            this.cdr.detectChanges();
          }
        );

        // Create blob URL
        this.blobUrl = URL.createObjectURL(blob);
        this.safeBlobUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.blobUrl
        );
      }

      // Handle video initialization immediately to avoid Angular timing issues
      if (isVideoFile) {
        this.createFallbackVideoPlayer();
      } else {
        // For non-video files, use template approach as usual
        setTimeout(() => {
          this.initializeMediaElements();
        }, 100);
      }

      // Also trigger change detection to ensure template renders properly
      this.cdr.detectChanges();

      this.setLoading(false);
      this.cdr.markForCheck(); // Force Angular to check for changes
    } catch (error: any) {
      this.errorMessage = 'Kunne ikke indlæse fil: ' + error.message;
      this.setLoading(false);
    }
  }

  /**
   * Initialize video/audio elements with proper event handlers
   */
  private initializeMediaElements(): void {
    // Video elements - use streaming if it's a video
    const videoElements = document.querySelectorAll('video');
    const isVideoFile =
      this.fileInfo?.mime_type?.startsWith('video/') ||
      this.fileInfo?.is_video === true;

    // If no video elements found but this is a video file, try again (max 2 retries)
    if (isVideoFile && videoElements.length === 0 && this.retryCount < 2) {
      this.retryCount++;
      const delay = 200; // Simple 200ms delay
      setTimeout(() => this.initializeMediaElements(), delay);
      return;
    }

    if (isVideoFile && videoElements.length === 0 && this.retryCount >= 2) {
      console.error(
        '[MediaPreview] Failed to find video elements after 2 retries, trying fallback approach...'
      );

      // Fallback: create video element programmatically and try streaming
      this.createFallbackVideoPlayer();
      return;
    }

    videoElements.forEach((video) => {
      if (isVideoFile) {
        // Use streaming for videos
        this.streamVideoToElement(video);
      } else if (this.blobUrl) {
        // Fallback for non-video content
        video.src = this.blobUrl;
        video.load();
      }

      // Add event listeners
      video.addEventListener('play', () => {
        this.isVideoPlaying = true;
        this.cdr.detectChanges();
      });

      video.addEventListener('pause', () => {
        this.isVideoPlaying = false;
        this.cdr.detectChanges();
      });

      video.addEventListener('ended', () => {
        this.isVideoPlaying = false;
        this.cdr.detectChanges();
      });

      this.videoElement = video;
    });

    // Audio elements
    const audioElements = document.querySelectorAll('audio');
    audioElements.forEach((audio) => {
      if (this.blobUrl) {
        audio.src = this.blobUrl;
        audio.load();
        this.audioElement = audio;
      }
    });
  }

  /**
   * Fallback: Create video player manually when template fails
   */
  private async createFallbackVideoPlayer(): Promise<void> {

    try {
      // Wait for Angular to render the media-content container
      this.waitForMediaContent().then((container) => {
        if (container) {
          this.insertVideoElement(container);
        } else {
          console.error(
            '[MediaPreview] Media-content container not found after waiting'
          );
          this.errorMessage = 'Could not create video player';
          this.setLoading(false);
        }
      });
    } catch (error: any) {
      console.error('[MediaPreview] Fallback video creation failed:', error);
      this.errorMessage = 'Could not create video player: ' + error.message;
      this.setLoading(false);
    }
  }

  /**
   * Wait for Angular to render the media-content container
   */
  private async waitForMediaContent(): Promise<Element | null> {
    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 10;

      const checkForContainer = () => {
        attempts++;
        const container = document.querySelector('.media-content');

        if (container) {
          resolve(container);
        } else if (attempts < maxAttempts) {
          setTimeout(checkForContainer, 100); // Wait 100ms between attempts
        } else {
          console.error(
            '[MediaPreview] Media-content container not found after',
            maxAttempts,
            'attempts'
          );
          resolve(null);
        }
      };

      // Start checking immediately
      checkForContainer();
    });
  }

  /**
   * Insert video element into the given container
   */
  private async insertVideoElement(container: Element | null): Promise<void> {
    if (!container) {
      console.error('[MediaPreview] No container found for video element');
      return;
    }

    const videoContainer = document.createElement('div');
    videoContainer.className = 'video-container fallback-video';

    const videoElement = document.createElement('video');
    videoElement.className = 'preview-video';
    videoElement.controls = true;
    videoElement.preload = 'metadata';
    videoElement.style.width = '100%'; // Start at full width
    videoElement.style.maxHeight = 'calc(100vh - 120px)'; // Prevent overflow
    videoElement.style.height = 'auto'; // Maintain aspect ratio
    videoElement.style.objectFit = 'contain'; // Ensure video fits properly

    videoContainer.appendChild(videoElement);

    // Add the video container at the beginning of the media-content area
    const firstChild = container.firstChild;
    if (firstChild) {
      container.insertBefore(videoContainer, firstChild);
    } else {
      container.appendChild(videoContainer);
    }

    // Try streaming first (it should work even with programmatically created elements)
    try {
      await this.streamVideoToElement(videoElement);
    } catch (streamingError) {

      // Fallback to blob download if streaming fails
      const blob = await this.mediaService.downloadFile(this.obfuscatedName);
      const blobUrl = URL.createObjectURL(blob);
      videoElement.src = blobUrl;
    }

    // Set loading state
    this.setLoading(false);

  }

  /**
   * Stream video using MediaService streaming capabilities
   */
  private async streamVideoToElement(
    videoElement: HTMLVideoElement
  ): Promise<void> {

    try {
      await this.mediaService.streamVideo(
        this.obfuscatedName,
        videoElement,
        (progress) => {
          this.cdr.detectChanges();
        }
      );
    } catch (error: any) {
      console.error('[MediaPreview] Video streaming failed:', error);

      // Fallback to regular download if streaming fails
      try {
        const blob = await this.mediaService.downloadFile(this.obfuscatedName);
        const blobUrl = URL.createObjectURL(blob);
        videoElement.src = blobUrl;
        videoElement.load();
      } catch (fallbackError: any) {
        console.error('[MediaPreview] Fallback also failed:', fallbackError);
        this.errorMessage = 'Could not load video: ' + fallbackError.message;
      }
    }
  }

  /**
   * Toggle video playback
   */
  toggleVideoPlayback(): void {
    if (this.videoElement) {
      if (this.isVideoPlaying) {
        this.videoElement.pause();
      } else {
        this.videoElement.play();
      }
    }
  }

  /**
   * Download the file
   */
  async downloadFile(): Promise<void> {
    if (!this.fileInfo) return;

    try {
      this.setLoading(true);

      // Download file using MediaService (handles streaming and regular files)
      const filename =
        this.fileInfo.display_name || this.fileInfo.original_name || 'download';
      const blob = await this.mediaService.downloadFile(this.obfuscatedName);

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);
      this.setLoading(false);
    } catch (error: any) {
      this.errorMessage = 'Download failed: ' + error.message;
      this.setLoading(false);
    }
  }

  /**
   * Go back to previous page
   */
  goBack(): void {
    // Go back to the previous page in browser history
    window.history.back();
  }

  /**
   * Close preview (same as go back for now)
   */
  closePreview(): void {
    this.goBack();
  }

  /**
   * Check if file is an image
   */
  isImage(): boolean {
    return this.fileInfo?.mime_type?.startsWith('image/') ?? false;
  }

  /**
   * Check if file is a PDF
   */
  isPdf(): boolean {
    return this.fileInfo?.mime_type === 'application/pdf';
  }

  /**
   * Get current mime type for template binding
   */
  getCurrentMimeType(): string | null {
    return this.fileInfo?.mime_type || null;
  }

  /**
   * Check if file is a video
   */
  isVideo(): boolean {
    const result =
      this.fileInfo?.mime_type?.startsWith('video/') ||
      this.fileInfo?.is_video === true;
    return result;
  }

  /**
   * Check if file is audio
   */
  isAudio(): boolean {
    return this.fileInfo?.mime_type?.startsWith('audio/') ?? false;
  }

  /**
   * Check if file supports inline viewing
   */
  supportsInlineViewing(): boolean {
    return this.isImage() || this.isPdf() || this.isVideo() || this.isAudio();
  }

  /**
   * Check if file is a document that should be downloaded
   */
  isDocument(): boolean {
    const mimeType = this.fileInfo?.mime_type || '';
    return (
      mimeType.includes('application/msword') ||
      mimeType.includes('application/vnd.openxmlformats-officedocument') ||
      mimeType.includes('application/vnd.ms-excel') ||
      mimeType.includes('application/vnd.ms-powerpoint') ||
      mimeType.includes('text/') ||
      mimeType.includes('application/rtf') ||
      mimeType.includes('application/vnd.oasis.opendocument')
    );
  }

  /**
   * Check if file is unsupported and should show download option
   */
  isUnsupportedFile(): boolean {
    return !this.supportsInlineViewing() && !this.isDocument();
  }

  /**
   * Get file size formatted
   */
  getFileSize(): string {
    if (!this.fileInfo?.file_size) return '-';
    return this.formatFileSize(this.fileInfo.file_size);
  }

  /**
   * Get formatted date
   */
  getFileDate(): string {
    if (!this.fileInfo?.created_at) return '-';
    const date = new Date(this.fileInfo.created_at);
    return date.toLocaleDateString('da-DK', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Format file size helper
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Get file metadata using Upload endpoint
   */
  private async getFileMetadata(obfuscatedName: string): Promise<FileItemDTO> {
    const metadata: UploadMetadataDTO = await this.mediaService.getMetadata(
      obfuscatedName
    );

    // Convert UploadMetadataDTO to FileItemDTO for compatibility
    const fileItem: FileItemDTO = {
      upload_id: 0, // Not available from metadata
      obfuscated_name: metadata.obfuscated_name,
      original_name: metadata.original_name,
      display_name: metadata.original_name, // Use original name as display name if not set
      mime_type: metadata.mime_type,
      file_size: metadata.file_size,
      is_video: metadata.is_video || false,
      is_fragmented: metadata.is_fragmented || false,
      video_duration: metadata.video_duration,
      created_at: metadata.created_at || new Date().toISOString(),
      updated_at: metadata.created_at || new Date().toISOString(),
      uploaded_by: metadata.uploaded_by || undefined,
      status: 'ready',
      parent_folder: '',
      version_of: undefined,
      deleted_at: undefined,

      // Syncfusion FileManager format
      name: metadata.original_name,
      type: 'file',
      size: metadata.file_size,
      dateModified: metadata.created_at || new Date().toISOString(),
      dateCreated: metadata.created_at || new Date().toISOString(),
      hasChild: false,
      isFile: true,
      filterPath: metadata.original_name,
    };

    return fileItem;
  }

  /**
   * Set loading state
   */
  private setLoading(loading: boolean): void {
    this.isLoading = loading;
    this.cdr.detectChanges();
  }

  /**
   * Clean up resources on destroy
   */
  ngOnDestroy(): void {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
    }
  }
}
